/*
Copyright (c) 2015, salesforce.com, inc. All rights reserved.
Copyright (c) 2019, Picsa Advisory Services (Pty) Ltd. All rights reserved.

Redistribution and use in source and binary forms, with or without modification, are permitted provided that the following conditions are met:
Redistributions of source code must retain the above copyright notice, this list of conditions and the following disclaimer.
Redistributions in binary form must reproduce the above copyright notice, this list of conditions and the following disclaimer in the documentation and/or other materials provided with the distribution.
Neither the name of salesforce.com, inc. nor the names of its contributors may be used to endorse or promote products derived from this software without specific prior written permission.

THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS "AS IS" AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE COPYRIGHT OWNER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES LOSS OF USE, DATA, OR PROFITS OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
*/

import React from 'react'
import PropTypes from 'prop-types'
import { Query } from 'react-apollo'
import gql from 'graphql-tag'
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome'
import { faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons'
import moment from 'moment'

import {
  Modal,
  Spinner,
  SvgIcon,
  LeftArrow,
  CreditApplicationPropertiesBar,
  CreateCaseModal,
  CreditApplicationInfo as Info,
  CreditApplicationIncome as Income,
  CreditApplicationSecurity as Security,
  LoanApplicationExpenses as Expenses,
  LoanApplicationLoans as Loans,
  CreditApplicationCreditReport as CreditReport,
  CreditApplicationRetail as Retail,
  CreditApplicationRequestedLoan as RequestedLoan,
  CreditApplicationReview as Review,
  CreditApplicationFinalise as Finalise,
  CreditApplicationIssue as Issue,
  CreditApplicationDisburse as DisburseAndActivate,
  RelatedFiles as FileViewer,
  Warning,
  ExpandableOverviewItem,
  LoanApplicationFileTypeFilter,
  Button
} from '../'

import { Calculations } from './Calculations'

import { CalculationVariables } from './CalculationVariables'

import {
  decodeBase64,
  encodeBase64,
  hasResponsibility
} from '../../utils'

import { QUERY_CASERELATIONS } from '../../queries'

import {
  CONTENTTYPE_FILE,
  CONTENTTYPE_REQUESTRUN,
  CONTENTTYPE_EMAIL,
  CONTENTTYPE_CREDITAPPLICATION
} from '../../constants'

const QUERY_CREDITREPORTACCOUNTS = gql`
  query($creditReport: UUID!) {
    creditreportaccounts(creditreport_Id: $creditReport) {
      edges {
        node {
          id
          balance
          instalment
          paymentHistory
          paymentFreqDesc
          creditreportsupplier {
            id
            name
            classification
            secured
            instalmentIsBalance
          }
          consolidation {
            edges {
              node {
                id
                amount
              }
            }
          }
        }
      }
    }
  }
`

// the following ref used to update the case count on the properties bar after a new case have been created
const creditApplicationPropertiesBarRef = React.createRef()
/*
the following two refs are used interval determining when to close their respective modal components when
a click outside their corresponding components happens;
see componentDidMount, componentWillUnmount & handleClickOutside
*/
const creditApplicationModalRef = React.createRef()
const createCaseModalRef = React.createRef()


export default class CreditApplicationModal extends React.PureComponent {
  /*
  In terms of layout, this modal component basically shows a hidable file viewer on the left, and steps
  in the credit application process as expandable sections on the right having a properties bar on the top.

  Step/sections will indicate progress and provide feedback via their icons.  They might sometimes also be
  locked/inaccessible based on the application state.
  */
  static propTypes = {
    creditApplication: PropTypes.object.isRequired,
    refetchData: PropTypes.func.isRequired,
    updateCreditApplication: PropTypes.func.isRequired,
    createCredit: PropTypes.func.isRequired,
    updateCredit: PropTypes.func.isRequired,
    markFileAsTrash: PropTypes.func,
    onClose: PropTypes.func.isRequired
  }

  state = {
    infoOpen: false,
    payslipsOpen: false,
    expensesOpen: false,
    loansOpen: false,
    creditReportOpen: false,
    retailOpen: false,
    securityOpen: false,
    calculationsOpen: false,
    requestedLoansOpen: false,
    approveOpen: false,
    finaliseOpen: false,
    issueOpen: false,
    disburseOpen: false,
    showCreateCaseModal: false,
    assignedTo: null,
    selectedFile: null,
    fileViewerFile: null,
    fileViewerFileTypeShortCode: null,
    showFileViewer: true,
    showCalcVariables: false,
    fileViewerToggleIconSizeMultiplier: 2,
    calcVariableToggleIconSizeMultiplier: 2
  }

  componentDidMount() {
    document.addEventListener('mousedown', this.handleClickOutside)
  }

  componentWillUnmount() {
    document.removeEventListener('mousedown', this.handleClickOutside)
  }

  handleClickOutside = (e) => {
    if (creditApplicationModalRef.current && !creditApplicationModalRef.current.contains(e.target) && !createCaseModalRef.current) {
      // click is outside, close drop-down(s)
      this.props.onClose()
    }

    if (createCaseModalRef.current && !createCaseModalRef.current.contains(e.target)) {
      // click is outside, close drop-down(s)
      this.closeCreateCaseModal()
    }
  }

  getCaseRelations = (file, requestRun) => {
    let relations = []
    if (requestRun) {
      if (requestRun.sources) {
        requestRun.sources.forEach(source => {
          relations.push({ objectId: decodeBase64(source.id), contentType: CONTENTTYPE_FILE })
          this.addEmailRelation(source, relations)
        })
      }
      relations.push({ objectId: decodeBase64(requestRun.id), contentType: CONTENTTYPE_REQUESTRUN })
    } else {
      relations.push({ objectId: decodeBase64(file.id), contentType: CONTENTTYPE_FILE })
      this.addEmailRelation(file, relations)
    }

    return relations
  }

  addEmailRelation = (file, relations) => {
    if (file.relations.edges.length > 0) {
      let emailRelation = file.relations.edges.find(relation => decodeBase64(relation.node.contentType.id) === CONTENTTYPE_EMAIL.toString())
      // if file has a filerelation to an email, add caserelation to email
      if (emailRelation) {
        relations.push({ objectId: encodeBase64(emailRelation.node.objectId, 'EmailNode'), contentType: CONTENTTYPE_EMAIL })
      }
    }
  }

  saveAssignedTo = (identity) => {
    this.props.updateCreditApplication({ assignedTo: identity })
  }

  handleSelectedFileChange = (file) => this.setState({ fileViewerFile: file, selectedFile: file, fileViewerFileTypeShortCode: null })

  closeCreateCaseModal = () => this.setState({ showCreateCaseModal: false })

  changeFileType = (fileTypeShortCode) => this.setState({ fileViewerFileTypeShortCode: fileTypeShortCode, selectedFile: null, fileViewerFile: null })

  toggleFileViewer = () => (
    this.setState(prevState => (
      {
        showFileViewer: !prevState.showFileViewer,
        showCalcVariables: false
      }
    ))
  )
  toggleCalcVariables = () => (
    this.setState(prevState => (
      {
        showCalcVariables: !prevState.showCalcVariables,
        showFileViewer: false
      }
    ))
  )

  hoverOverSidebarIcon = (e) => {
    const toggle = e.target.id
    this.setState({ [`${toggle}IconSizeMultiplier`]: 3 })
  }
  resetSizebarIconSize = (e) => {
    const toggle = e.target.id
    this.setState({ [`${toggle}IconSizeMultiplier`]: 2 })
  }

  render() {
    const { creditApplication, onClose, updateCreditApplication, createCredit, updateCredit, refetchData } = this.props

    if (creditApplication) {
      const client = creditApplication.client
      const credit = creditApplication.credits.edges.length > 0
        ? creditApplication.credits.edges[0].node
        : null
      let creditWithCmsId = credit && credit.cmsId
        ? credit
        : null
      let creditWorkflowRunProcessing = creditWithCmsId && creditWithCmsId.workflowrunProcessing
        ? credit.workflowrunProcessing
        : null

      const { creditType, amountAppliedFor, overrideEligibleLoanAmount } = creditApplication
      const userHasOverrideResponsibility = hasResponsibility('self', 'loanapplication_override')
      const updatedAt = creditApplication.updatedAt
        ? moment(creditApplication.updatedAt).format('YYYYMMDDhhmmss')
        : '-'
      const cashOrRetailCreditType = ['CASH', 'RETAIL_CASH', 'RETAIL_DIRECT'].indexOf(creditType) > -1

      return (
        <Modal
          ref={creditApplicationModalRef}
          body={this.state.showCreateCaseModal
            ? <CreateCaseModal
                forwardedRef={createCaseModalRef}
                productType='loans'
                requestType='Application'
                followUp={true}
                affects={client ? client.identity.id : null}
                caseRelations={[ { objectId: decodeBase64(creditApplication.id),  contentType: CONTENTTYPE_CREDITAPPLICATION }]}
                onCloseClick={this.closeCreateCaseModal}
                onSaveClick={this.closeCreateCaseModal}
                onCaseRelationsCreated={creditApplicationPropertiesBarRef.current.props.updateData}
              />
            : <div className={`credit-application-modal${this.state.showFileViewer || this.state.showCalcVariables ? '' : ' viewer-closed'} slds-size_1-of-1`}>
                <div className='loan-application-modal-nav slds-border_bottom page-navigation'>
                  <div className='u-font-size-larger clickable' onClick={this.props.onClose}>
                    <LeftArrow />
                    Back
                  </div>
                </div>
                {cashOrRetailCreditType && this.state.showCalcVariables &&
                  <div className='slds-scrollable_y loan-application-modal-file-view'>
                    <CalculationVariables
                      creditApplicationId={creditApplication.id}
                      calculationsLocked={creditApplication.locked}
                      calculationsSnapshot={creditApplication.calculationsCamelcase}
                      key={`calculationVariables_${updatedAt}`}
                    />
                  </div>
                }
                {this.state.showFileViewer &&
                  <div className='slds-scrollable_y loan-application-modal-file-view'>
                    {/* file type filter component below allows selection of file type to view in file viewer*/}
                    <LoanApplicationFileTypeFilter
                      value={this.state.fileViewerFileTypeShortCode}
                      onChange={this.changeFileType}
                      allOnly={this.state.fileViewerFile}
                    />
                    {/* file viewer displays the files linked to the credit application */}
                    <FileViewer
                      objectId={decodeBase64(creditApplication.id)}
                      fileUploaded={this.state.fileUploaded}
                      preview={true}
                      file={this.state.fileViewerFile}
                      fileTypeShortCode={this.state.fileViewerFileTypeShortCode}
                      selectFile={this.state.selectedFile}
                      displayChildren={false}
                      onClick={file => this.setState({ selectedFile: file })}
                    />
                  </div>
                }
                <div className='credit-application-modal_docked-sidebar slds-border_right slds-text-color_inverse'>
                  <div
                    id='fileViewerToggle'
                    onMouseEnter={this.hoverOverSidebarIcon}
                    onMouseLeave={this.resetSizebarIconSize}
                    onClick={this.toggleFileViewer}
                    className='slds-text-align_center slds-p-around_xx-small clickable'>
                    {this.state.showFileViewer
                      ? <Icon icon={faEyeSlash} size={`${this.state.fileViewerToggleIconSizeMultiplier}x`} inverse={true} />
                      : <Icon icon={faEye} size={`${this.state.fileViewerToggleIconSizeMultiplier}x`} inverse={true} />
                    }
                    <div style={{ lineHeight: 1.1 }}>
                      {this.state.showFileViewer
                        ? 'Hide file viewer'
                        : 'Show file viewer'
                      }
                    </div>
                  </div>
                  {/* if someone has permission to override the application, calculation variables can also be view in place of the file viewer */}
                  {cashOrRetailCreditType && userHasOverrideResponsibility &&
                    <div className='slds-m-top_large'>
                      <div
                        id='calcVariableToggle'
                        onMouseEnter={this.hoverOverSidebarIcon}
                        onMouseLeave={this.resetSizebarIconSize}
                        onClick={this.toggleCalcVariables}
                        className='slds-text-align_center slds-p-around_xx-small clickable'>
                        {this.state.showCalcVariables
                          ? <Icon icon={faEyeSlash} size={`${this.state.calcVariableToggleIconSizeMultiplier}x`} inverse={true} />
                          : <Icon icon={faEye} size={`${this.state.calcVariableToggleIconSizeMultiplier}x`} inverse={true} />
                        }
                        <div style={{ lineHeight: 1.1 }}>
                          {this.state.showCalcVariables
                            ? 'Hide calc variables'
                            : 'Show calc variables'
                          }
                        </div>
                      </div>
                    </div>
                  }
                </div>
                <div className='slds-scrollable_y loan-application-modal-detail slds-size_1-of-1 slds-border_left' style={{ height: '100%' }}>
                  <React.Fragment>
                    <Query query={QUERY_CASERELATIONS} variables={{ objectId: decodeBase64(creditApplication.id) }}>
                      {({ loading, error, data: crData, refetch }) => {
                        if (loading) {
                          return <Spinner className='slds-is-relative slds-m-left_large' size='x-small' />
                        }
                        if (error) {
                          return (
                            <Warning iconSize='x-small' inline={true} className='slds-m-left_small slds-m-right_small'>
                              Failed to load case relation information.
                            </Warning>
                          )
                        }

                        let openCases = crData.caserelations.edges.filter(caseRelation => (
                          caseRelation.node.case.status === 'OPEN'
                        ))
                        let resolvedCases = crData.caserelations.edges.filter(caseRelation => (
                          caseRelation.node.case.status === 'RESOLVED'
                        ))

                        return (
                          /* the properties bar shows
                          - the name of the person the application for (can navigate to identity page via link)
                          - the assignee (can be changed)
                          - the number of open/resolved cases for the credit application (new cases can be created via 'create' button)
                          */
                          <CreditApplicationPropertiesBar
                            ref={creditApplicationPropertiesBarRef}
                            creditApplication={creditApplication.id}
                            creditApplicationStatus={creditApplication.status}
                            assignedToIdentity={creditApplication.assignedTo}
                            client={client}
                            openCases={openCases}
                            resolvedCases={resolvedCases}
                            onCreateCaseClick={() => this.setState({ showCreateCaseModal: true })}
                            assignedToSave={this.saveAssignedTo}
                            updateData={refetch}
                          />
                        )
                      }}
                    </Query>
                    <div className='slds-m-top_large slds-m-bottom_large slds-m-right_large'>
                      {/* Info section displays loan amount applied for and basic client information */}
                      <Info
                        identity={client.identity}
                        creditType={creditApplication.creditType}
                        creditApplicationStatus={creditApplication.status}
                        employmentType={creditApplication.client.employmentType}
                        preferredCallTime={creditApplication.calltime}
                        preferredContactNumber={creditApplication.client.identity.addresses.edges.length > 0
                          ? creditApplication.client.identity.addresses.edges[0].node.addressValue
                          : null
                        }
                        bankName={creditApplication.bankaccount ? creditApplication.bankaccount.bankName : null}
                        bankAccountNumber={creditApplication.bankaccount ? creditApplication.bankaccount.accountNumber: null}
                        amountAppliedFor={amountAppliedFor}
                        open={this.state.infoOpen}
                        updateCreditApplication={updateCreditApplication}
                        onClick={() => this.setState(prevState => ({ infoOpen: !prevState.infoOpen }))}
                        onComplete={() => this.setState({ infoOpen: false, payslipsOpen: true })}
                      />
                      {/* Income section displays information on the 3 payslips uploaded as a requirement for the application */}
                      <Income
                        client={client}
                        employmentCommencedAt={creditApplication.employmentCommencedAt}
                        selectablePayslips={creditApplication.selectablePayslips}
                        selectedPayslips={creditApplication.selectedPayslips}
                        missingPayslipDates={creditApplication.missingPayslipDates}
                        updateCreditApplication={updateCreditApplication}
                        open={this.state.payslipsOpen}
                        locked={creditApplication.locked}
                        onClick={() => this.setState(prevState => ({ payslipsOpen: !prevState.payslipsOpen }))}
                        onComplete={() => this.setState({ payslipsOpen: false, expensesOpen: true })}
                      />
                      {/* Expenses sections shows expense categories and amounts */}
                      <Expenses
                        expenses={creditApplication.expenses.edges.map(expense => expense.node)}
                        open={this.state.expensesOpen}
                        onClick={() => this.setState(prevState => ({ expensesOpen: !prevState.expensesOpen }))}
                        onComplete={() => this.setState({ expensesOpen: false, loansOpen: true, creditReportOpen: true })}
                      />
                      {/* Loans section shows current Picsa loans the client has */}
                      <Loans
                        client={client.id}
                        clientLoans={[...client.loans.edges, ...client.credits.edges]}
                        open={this.state.loansOpen}
                        onClick={() => this.setState(prevState => ({ loansOpen: !prevState.loansOpen }))}
                        onComplete={() => this.setState({ loansOpen: false, creditReportOpen: true })}
                      />
                      {creditApplication.creditreport
                        ? <Query
                            query={QUERY_CREDITREPORTACCOUNTS}
                            variables={{
                              creditReport: decodeBase64(creditApplication.creditreport.id)
                            }}>
                            {({ loading, error, data }) => {
                              if (loading) {
                                return (
                                  <ExpandableOverviewItem
                                    title='Credit report'
                                    summary={
                                      <div style={{ width: '100px' }}>
                                        <Spinner size='x-small' className='slds-is-relative slds-m-top_medium' />
                                      </div>
                                    }
                                    open={false}
                                  />
                                )
                              }
                              if (error) {
                                return (
                                  <ExpandableOverviewItem
                                    title='Credit report'
                                    summary={<Warning>Failed to load credit report data.</Warning>}
                                    open={false}
                                  />
                                )
                              }
                              return (
                                // Credit report section shows credit accounts that can be consolidated
                                <CreditReport
                                  creditReport={creditApplication.creditreport}
                                  creditApplicationId={creditApplication.id}
                                  creditType={creditApplication.creditType}
                                  identity={client.identity}
                                  accounts={data.creditreportaccounts
                                    ? data.creditreportaccounts.edges.map(account => account.node)
                                    : []
                                  }
                                  consolidations={creditApplication.consolidations
                                    ? creditApplication.consolidations.edges.map(consolidation => consolidation.node)
                                    : []
                                  }
                                  open={this.state.creditReportOpen}
                                  selectedFile={this.state.selectedFile}
                                  changeSelectedFile={this.handleSelectedFileChange}
                                  refetchData={refetchData}
                                  onClick={() => this.setState(prevState => ({ creditReportOpen: !prevState.creditReportOpen }))}
                                  onComplete={() => this.setState({ creditReportOpen: false, securityOpen: true })}
                                />
                              )
                            }}
                          </Query>
                        : <CreditReport
                            creditReport={null}
                            creditApplicationId={creditApplication.id}
                            identity={client.identity}
                            accounts={[]}
                            consolidations={[]}
                            open={this.state.creditReportOpen}
                            selectedFile={this.state.selectedFile}
                            changeSelectedFile={this.handleSelectedFileChange}
                            refetchData={refetchData}
                            onClick={() => this.setState(prevState => ({ creditReportOpen: !prevState.creditReportOpen }))}
                            onComplete={() => this.setState({ creditReportOpen: false, securityOpen: true })}
                          />
                      }
                      {amountAppliedFor > 0 && creditType.includes('RETAIL') &&
                        /* Retail non-expandable section will only show if retail loan applied for; it indicates the number of retail invoices received.*/
                        <Retail creditApplication={creditApplication.id} />
                      }
                      {/* Calculations section shows key inputs and variables that affect loan eligibility. The loan term can also be chosen in this section */}
                      {creditType !== 'EMERGENCY' &&
                        <Calculations
                          client={client}
                          creditApplication={creditApplication}
                          updateCredit={this.props.updateCredit}
                          creditType={creditApplication.creditType}
                          updateCreditApplication={this.props.updateCreditApplication}
                          open={this.state.calculationsOpen}
                          refetchData={refetchData}
                          onClick={() => this.setState(prevState => ({ calculationsOpen: !prevState.calculationsOpen }))}
                          userHasOverrideResponsibility={userHasOverrideResponsibility}
                          key={`calculations_${updatedAt}`}
                        />
                      }
                      {/* Security section shows long-term investment balance as well as what is required. EarnBacks points balance is also displayed.*/}
                      <Security
                        creditApplicationId={creditApplication.id}
                        open={this.state.securityOpen}
                        calculationsLocked={creditApplication.locked}
                        calculationsSnapshot={creditApplication.calculationsCamelcase}
                        onClick={() => this.setState(prevState => ({ securityOpen: !prevState.securityOpen }))}
                        onComplete={() => this.setState({ securityOpen: false, calculationsOpen: true })}
                        key={`security_${overrideEligibleLoanAmount}`}
                      />
                      {/* Request loan section allows the input of loan amount, instalment & loan management system id.  This will mirror the Mycomax entry. */}
                      <RequestedLoan
                        credit={credit}
                        creditType={creditApplication.creditType}
                        amountEligibleFor={creditApplication.overrideStatus === null
                          ? creditApplication.eligibleLoanAmount
                          : creditApplication.overrideEligibleLoanAmount
                        }
                        createCredit={createCredit}
                        updateCredit={updateCredit}
                        refetchData={refetchData}
                        open={this.state.requestedLoansOpen}
                        onClick={() => this.setState(prevState => ({ requestedLoansOpen: !prevState.requestedLoansOpen }))}
                        onComplete={() => this.setState({ requestedLoansOpen: false, finaliseOpen: true })}
                        key={JSON.stringify(credit)}
                      />
                      {/* Finalise section. Outcome can be selected and the review of application assigned to a person. */}
                      <Finalise
                        outcome={creditApplication.outcome}
                        outcomeNotes={creditApplication.outcomeNotes}
                        finalisedBy={creditApplication.finalisedBy}
                        finalisedAt={creditApplication.finlisedAt}
                        credit={credit}
                        updateCreditApplication={updateCreditApplication}
                        open={this.state.finaliseOpen}
                        onClick={() => this.setState(prevState => ({ finaliseOpen: !prevState.finaliseOpen }))}
                        onComplete={() => this.setState({ finaliseOpen: false, approveOpen: true })}
                      />
                      {/* In the review section the application can be rejected or approved */}
                      <Review
                        creditApplication={creditApplication.id}
                        creditApplicationStatus={creditApplication.status}
                        outcome={creditApplication.outcome}
                        status={creditApplication.status}
                        assignedTo={creditApplication.assignedTo}
                        approvedBy={creditApplication.approvedBy}
                        approvedAt={creditApplication.approvedAt}
                        approverNotes={creditApplication.approverNotes}
                        finalisedBy={creditApplication.finalisedBy}
                        updateCreditApplication={updateCreditApplication}
                        open={this.state.approveOpen}
                        onClick={() => this.setState(prevState => ({ approveOpen: !prevState.approveOpen }))}
                        onComplete={() => this.setState({ approveOpen: false, issueOpen: true })}
                        onRejected={() => this.setState({ finaliseOpen: true, approveOpen: false })}
                      />
                      {/* Issue section is where the loan disbursal workflow can be kicked off. Alternatively, finalisation can be removed */}
                      <Issue
                        creditApplication={creditApplication}
                        credit={creditWithCmsId}
                        issuedBy={creditApplication.issuedBy}
                        issuerNotes={creditApplication.issuerNotes}
                        approverNotes={creditApplication.approverNotes}
                        outcomeNotes={creditApplication.outcomeNotes}
                        status={creditApplication.status}
                        updateCreditApplication={updateCreditApplication}
                        updateCredit={updateCredit}
                        open={this.state.issueOpen}
                        onClick={() => this.setState(prevState => ({ issueOpen: !prevState.issueOpen }))}
                        collapseSectionOnComplete={() => this.setState({ issueOpen: false })}
                      />
                      {/* The "Disburse and activate" section display the progress of the loan disbursal workflow */}
                      <DisburseAndActivate
                        outcome={creditApplication.outcome}
                        issuedAt={creditApplication.issuedAt}
                        issuedBy={creditApplication.issuedBy}
                        status={creditApplication.status}
                        creditId={creditWithCmsId
                          ? creditWithCmsId.id
                          : null
                        }
                        creditWorkflowRunProcessing={creditWorkflowRunProcessing}
                        open={this.state.disburseOpen}
                        refetchData={refetchData}
                        onClick={() => this.setState(prevState => ({ disburseOpen: !prevState.disburseOpen }))}
                      />
                    </div>
                  </React.Fragment>
                </div>
              </div>
          }
          sizeXLarge={true}
          contentPadding='none'
        />
      )
    } else {
      return (
        <div className='loan-application-modal slds-size_1-of-1'>
          <div className='loan-application-modal-nav slds-border_bottom page-navigation'>
            <Button onClick={onClose}>
              &larr;
              Back
            </Button>
          </div>
          <div className='loan-application-modal-file-view slds-text-align_center slds-p-top_large'>
            <div className='u-font-size-larger slds-text-color_inverse'>
              <SvgIcon
                variant='utility'
                symbol='info'
                colour='inverse'
                size='small'
                className='slds-m-right_small'
              />
              No files to display.
            </div>
          </div>
          <div className='slds-scrollable_y loan-application-modal-detail slds-size_1-of-1'>
            <Warning className='slds-m-top_large slds-m-left_large'>
              Failed to retrieve loan application data.
            </Warning>
          </div>
        </div>
      )
    }
  }
}



// WEBPACK FOOTER //
// ./src/components/creditapplication-modal/index.js